import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  Button,
  Header,
  Icon,
  Loader,
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "semantic-ui-react";

import { UpdateActiveClientField } from "data/hub/clients/actions";
import useClient from "data/hub/clients/useClient";
import useHub from "data/hub/useHub";

import { useBooleanState } from "hooks";
import { ConfirmationModal } from "components/ConfirmationModal";
import CRMIcon from "components/CRMIcon";
import Tooltip from "components/Tooltip";

import { useClientMeetings, useSyncClientMeetings } from "./hooks";
import { MeetingFormModal, MeetingsList, RecentMeeting } from "./components";

function SyncMeetingsCard() {
  const [meeting, setMeeting] = useState(null);
  const [isMeetingModalOpen, openMeetingModal, closeMeetingModal] =
    useBooleanState();
  const [
    isConfirmationModalOpen,
    openConfirmationModal,
    closeConfirmationModal,
  ] = useBooleanState();

  const { clientId } = useParams();
  const { meetings, recentMeetings, initialFetch, error, deleteMeeting } =
    useClientMeetings(clientId, closeConfirmationModal);

  const reloadMeetings = useCallback(() => {
    meetings.refetch();
    recentMeetings.reloadData();
  }, [meetings, recentMeetings]);

  const syncClientMeetings = useSyncClientMeetings(clientId, reloadMeetings);

  const clientState = useClient(clientId);
  const hub = useHub();
  const dispatch = useDispatch();

  useEffect(() => {
    if (clientState?.refreshMeetings) {
      reloadMeetings();
      dispatch(UpdateActiveClientField(clientId, { refreshMeetings: false }));
    }
  }, [clientState, reloadMeetings, dispatch, clientId]);

  const handleOnEdit = (m) => {
    setMeeting(m);
    openMeetingModal();
  };

  const handleOnDelete = (m) => {
    setMeeting(m);
    openConfirmationModal();
  };

  return (
    <React.Fragment>
      <div
        data-test="card-meetings"
        style={{
          display: "flex",
          flexDirection: "column",
          margin: "0.5em 1em",
          gap: "0.5em",
        }}
      >
        <header
          style={{
            marginBottom: "0.25em",
            display: "flex",
            gap: "0.5em",
            alignItems: "center",
          }}
        >
          <Header as="h5" style={{ marginBottom: "0" }}>
            Meeting Dates
          </Header>

          <CRMIcon
            crmType={hub.clientPool.type}
            size="small"
            title={`Your meetings are being synced with ${hub.clientPool.type}`}
          />

          {syncClientMeetings.inProgress && (
            <Loader size="mini" inline className="invertedLoader" />
          )}

          {!syncClientMeetings.inProgress && syncClientMeetings.error && (
            <Tooltip
              hoverable
              position="bottom center"
              title={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1em",
                  }}
                >
                  <span>
                    Failed to sync this clients meeting dates. Results may be
                    outdated.
                  </span>
                  <Button
                    size="small"
                    icon="redo"
                    content="Retry to sync meetings"
                    primary
                    onClick={syncClientMeetings.run}
                  />
                </div>
              }
            >
              <Icon name="warning sign" color="yellow" size="small" />
            </Tooltip>
          )}
        </header>

        {initialFetch && (
          <Placeholder>
            <PlaceholderParagraph>
              <PlaceholderLine length="long" />
              <PlaceholderLine length="long" />
            </PlaceholderParagraph>

            <PlaceholderParagraph>
              <PlaceholderLine length="short" />
              <PlaceholderLine length="short" />
            </PlaceholderParagraph>
          </Placeholder>
        )}

        {error && <span>Unable to load meetings</span>}

        {!initialFetch && !error && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.5em" }}
          >
            <RecentMeeting
              title="Next:"
              data-test="meeting-date-field-next"
              meeting={recentMeetings.data.next}
              onEdit={handleOnEdit}
              onDelete={handleOnDelete}
            />

            <RecentMeeting
              title="Last:"
              data-test="meeting-date-field-last"
              meeting={recentMeetings.data.last}
              onEdit={handleOnEdit}
              onDelete={handleOnDelete}
            />

            <Button
              basic
              size="small"
              disabled={syncClientMeetings.inProgress}
              style={{
                padding: "0",
                boxShadow: "none",
                fontWeight: "bold",
                width: "100px",
                height: "30px",
              }}
              onClick={() => {
                setMeeting(undefined);
                openMeetingModal();
              }}
            >
              <Icon name="calendar alternate" />
              Add Meeting
            </Button>

            <MeetingsList
              meetings={meetings}
              onEdit={handleOnEdit}
              onDelete={handleOnDelete}
            />
          </div>
        )}
      </div>

      {isMeetingModalOpen && (
        <MeetingFormModal
          meeting={meeting}
          onClose={closeMeetingModal}
          onDone={() => {
            meetings.refetch();
            recentMeetings.reloadData();
          }}
        />
      )}

      {isConfirmationModalOpen && (
        <ConfirmationModal
          title={`Delete ${meeting?.name}`}
          icon="remove"
          buttons={[
            {
              id: "cancel-button",
              text: "Cancel",
              disabled: deleteMeeting.loading,
              callBack: closeConfirmationModal,
            },
            {
              id: "merge-button",
              text: "Delete",
              color: "red",
              disabled: deleteMeeting.loading,
              callBack: async () => {
                await deleteMeeting.delete(meeting);
                meetings.refetch();
                recentMeetings.reloadData();
              },
            },
          ]}
        >
          <p>Are you sure you want to delete the meeting {meeting.name}?</p>
          <p>
            This action cannot be undone and will also delete the meeting from
            your CRM.
          </p>
        </ConfirmationModal>
      )}
    </React.Fragment>
  );
}

export default SyncMeetingsCard;
